import React, { useEffect, useRef, useMemo } from "react";
import { FiX, FiArrowUp, FiArrowDown, FiClock, FiWifi } from "react-icons/fi";
import { fmtBytes, fmtNum, ago } from "../utils/formatters";
import DeviceSpeedLimit from "./DeviceSpeedLimit";

// ── App color mapping ────────────────────────────────────────────────────────

const APP_COLORS = {
  APP_NETFLIX: "#E50914",
  APP_YOUTUBE: "#FF0000",
  APP_SPOTIFY: "#1DB954",
  APP_ZOOM: "#2D8CFF",
  APP_DISCORD: "#5865F2",
  APP_TWITCH: "#9146FF",
  APP_TIKTOK: "#ff0050",
  APP_INSTAGRAM: "#E4405F",
  APP_FACEBOOK: "#1877F2",
  APP_SLACK: "#4A154B",
  APP_MSTEAMS: "#6264A7",
  APP_APPLE: "#A2AAAD",
  APP_AMAZON_VIDEO: "#00A8E1",
  APP_HULU: "#1CE783",
  APP_DISNEYPLUS: "#113CCF",
  APP_HBO: "#991EEB",
  APP_STEAM: "#1b2838",
  APP_GITHUB: "#6e5494",
  APP_GOOGLE_MEET: "#00897B",
};

const CATEGORY_COLORS = {
  STREAMING: "#E50914",
  REALTIME: "#2D8CFF",
  INTERACTIVE: "#1DB954",
  BULK: "#F59E0B",
  CRITICAL: "#EF4444",
  BE: "#6366f1",
  DIAG: "#8B5CF6",
};

const getAppColor = (key) => APP_COLORS[key] || "#6366f1";
const getCategoryColor = (key) => CATEGORY_COLORS[key] || "#6366f1";

const friendlyAppName = (key) =>
  key
    .replace(/^APP_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// ── Component ────────────────────────────────────────────────────────────────

export default function DeviceDrawer({ device, nodeId, onClose, classifierMode }) {
  const drawerRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Focus the close button on mount for keyboard accessibility
  useEffect(() => {
    if (closeBtnRef.current) closeBtnRef.current.focus();
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Trap focus inside drawer
  useEffect(() => {
    const trap = (e) => {
      if (e.key !== "Tab" || !drawerRef.current) return;
      const focusable = drawerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", trap);
    return () => document.removeEventListener("keydown", trap);
  }, []);

  // Lock body scroll while drawer is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const isCatMode = classifierMode === "cat";
  const colorFn = isCatMode ? getCategoryColor : getAppColor;

  const appBreakdown = useMemo(() => {
    if (!device?.app_breakdown) return [];
    const entries = Object.entries(device.app_breakdown)
      .map(([key, value]) => ({ key, value, color: colorFn(key) }))
      .sort((a, b) => b.value - a.value);
    const total = entries.reduce((s, e) => s + e.value, 0);
    return entries.map((e) => ({
      ...e,
      pct: total > 0 ? (e.value / total) * 100 : 0,
      label: friendlyAppName(e.key),
    }));
  }, [device, colorFn]);

  const totalBytes = (device?.tx_bytes || 0) + (device?.rx_bytes || 0);
  const isStale = (device?.active_seconds ?? 0) > 60;
  const deviceName =
    device?.hostname && device.hostname !== "*"
      ? device.hostname
      : "Unknown Device";

  return (
    <>
      {/* Backdrop */}
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />

      {/* Drawer */}
      <div
        className="drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Device details: ${deviceName}`}
      >
        {/* Header */}
        <div className="drawer-header">
          <div className="drawer-header-info">
            <h2 className="drawer-title">{deviceName}</h2>
            <span className={`drawer-status ${isStale ? "stale" : "active"}`}>
              <span className="drawer-status-dot" />
              {isStale ? "Stale" : "Active"}
            </span>
          </div>
          <button
            className="drawer-close"
            onClick={onClose}
            ref={closeBtnRef}
            aria-label="Close device details"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Identity */}
        <div className="drawer-section">
          <div className="drawer-meta-grid">
            <div className="drawer-meta-item">
              <span className="drawer-meta-label">IP Address</span>
              <span className="drawer-meta-value">{device?.ip || "—"}</span>
            </div>
            <div className="drawer-meta-item">
              <span className="drawer-meta-label">MAC Address</span>
              <span className="drawer-meta-value mono">{device?.mac || "—"}</span>
            </div>
            <div className="drawer-meta-item">
              <span className="drawer-meta-label">
                <FiClock size={12} style={{ marginRight: 4 }} />
                Last Seen
              </span>
              <span className="drawer-meta-value">{ago(device?.last_seen)}</span>
            </div>
          </div>
        </div>

        <DeviceSpeedLimit nodeId={nodeId} device={device} deviceName={deviceName === "Unknown Device" ? "" : deviceName} />

        {/* Traffic summary */}
        <div className="drawer-section">
          <h3 className="drawer-section-title">Traffic</h3>
          <div className="drawer-traffic-cards">
            <div className="drawer-traffic-card upload">
              <FiArrowUp className="drawer-traffic-icon" size={18} />
              <div>
                <div className="drawer-traffic-label">Upload</div>
                <div className="drawer-traffic-value">{fmtBytes(device?.tx_bytes)}</div>
                <div className="drawer-traffic-pkts">{fmtNum(device?.tx_packets)} pkts</div>
              </div>
            </div>
            <div className="drawer-traffic-card download">
              <FiArrowDown className="drawer-traffic-icon" size={18} />
              <div>
                <div className="drawer-traffic-label">Download</div>
                <div className="drawer-traffic-value">{fmtBytes(device?.rx_bytes)}</div>
                <div className="drawer-traffic-pkts">{fmtNum(device?.rx_packets)} pkts</div>
              </div>
            </div>
          </div>
          <div className="drawer-total-bar">
            <span className="drawer-total-label">Total Traffic</span>
            <span className="drawer-total-value">{fmtBytes(totalBytes)}</span>
          </div>
        </div>

        {/* Application breakdown */}
        {appBreakdown.length > 0 && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{isCatMode ? "Category Breakdown" : "Application Breakdown"}</h3>

            {/* Mini donut ring */}
            <div className="drawer-donut-container">
              <svg
                width="120"
                height="120"
                viewBox="0 0 120 120"
                className="drawer-donut"
              >
                {(() => {
                  const r = 48;
                  const circ = 2 * Math.PI * r;
                  let offset = 0;
                  return appBreakdown.slice(0, 8).map((app, i) => {
                    const dash = (app.pct / 100) * circ;
                    const el = (
                      <circle
                        key={app.key}
                        cx="60"
                        cy="60"
                        r={r}
                        fill="none"
                        stroke={app.color}
                        strokeWidth="10"
                        strokeDasharray={`${dash} ${circ - dash}`}
                        strokeDashoffset={-offset}
                        opacity="0.85"
                        style={{
                          transform: "rotate(-90deg)",
                          transformOrigin: "60px 60px",
                          transition: "stroke-dasharray 0.5s ease",
                        }}
                      />
                    );
                    offset += dash;
                    return el;
                  });
                })()}
                <text
                  x="60"
                  y="56"
                  textAnchor="middle"
                  fill="#e2e8f0"
                  fontSize="13"
                  fontWeight="800"
                >
                  {appBreakdown.length}
                </text>
                <text
                  x="60"
                  y="70"
                  textAnchor="middle"
                  fill="#64748b"
                  fontSize="9"
                >
                  {isCatMode ? "categories" : "apps"}
                </text>
              </svg>
            </div>

            {/* App bars */}
            <div className="drawer-app-list">
              {appBreakdown.slice(0, 10).map((app) => (
                <div key={app.key} className="drawer-app-row">
                  <div className="drawer-app-info">
                    <span
                      className="drawer-app-dot"
                      style={{ background: app.color }}
                    />
                    <span className="drawer-app-name">{app.label}</span>
                  </div>
                  <div className="drawer-app-bar-wrap">
                    <div
                      className="drawer-app-bar"
                      style={{
                        width: `${Math.max(2, app.pct)}%`,
                        background: app.color,
                      }}
                    />
                  </div>
                  <div className="drawer-app-stats">
                    <span className="drawer-app-pct">
                      {app.pct.toFixed(1)}%
                    </span>
                    <span className="drawer-app-count">
                      {fmtNum(app.value)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {appBreakdown.length === 0 && (
          <div className="drawer-section">
            <h3 className="drawer-section-title">{isCatMode ? "Category Breakdown" : "Application Breakdown"}</h3>
            <div className="drawer-empty">
              <FiWifi size={24} style={{ opacity: 0.3 }} />
              <p>No classified traffic yet for this device.</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
